
import { GoogleGenAI } from "@google/genai";
import { PromptConfig, GeneratedImage, SelectionRect } from "../types";

const API_KEY = process.env.API_KEY;

const getClient = () => {
  // Always check process.env.API_KEY as it might be updated by window.aistudio selection
  if (!process.env.API_KEY) {
    // In a real scenario we might wait or throw, but here we expect it to be handled upstream or injected
    // For local dev without injection, this might throw.
    // throw new Error("API Key not found."); 
    console.warn("API Key is undefined in process.env. Check environment configuration.");
  }
  return new GoogleGenAI({ apiKey: process.env.API_KEY });
};

// Helper for API Key Selection (Veo requirement)
export const checkAndRequestApiKey = async (): Promise<void> => {
  const win = window as any;
  if (win.aistudio && win.aistudio.hasSelectedApiKey) {
    const hasKey = await win.aistudio.hasSelectedApiKey();
    if (!hasKey && win.aistudio.openSelectKey) {
       await win.aistudio.openSelectKey();
    }
  }
};

// Safety settings to allow medical anatomy (often flagged as gore/harassment by default)
const SAFETY_SETTINGS = [
  { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
];

// Helper to construct the rich prompt
export const constructMedicalPrompt = (config: PromptConfig, isVideo = false): string => {
  const baseSubject = config.focus ? config.focus : `${config.region} Region`;
  const systemContext = config.system ? `of the ${config.system} System` : '';
  
  let visualPrompt = isVideo 
    ? `Cinematic medical animation of the human ${baseSubject} ${systemContext}, showing functional anatomy in motion. `
    : `Anatomically accurate ${config.style} of the human ${baseSubject} ${systemContext}.`;
  
  if (config.region) visualPrompt += ` Region: ${config.region}.`;
  if (config.plane !== 'None (3D Default)') visualPrompt += ` Plane: ${config.plane} cross-section.`;
  if (config.view) visualPrompt += ` View: ${config.view}.`;
  if (config.zoom) visualPrompt += ` Scale: ${config.zoom}.`;
  if (config.customModifier) visualPrompt += ` Specific condition: ${config.customModifier}.`;
  
  visualPrompt += ` Lighting: ${config.lighting}. Background: ${config.background}.`;
  visualPrompt += ` High resolution, educational medical plate, scientifically accurate labels absent (clean image).`;

  // Negative Prompt Injection (Hidden constraints)
  visualPrompt += ` Ensure: No distorted text, no biological inaccuracies, no gore, no blur, no low resolution, no wrong proportions. Image must be clear and professional.`;

  if (config.referenceImageUrl && !isVideo) {
    visualPrompt += ` IMPORTANT: Use the attached image as a STRUCTURAL REFERENCE (Canny Edge style). strictly follow the composition, pose, and outlines of the reference sketch, but render it in the requested medical style.`;
  }

  return visualPrompt;
};

// --- Single Image Generation ---
export const generateMedicalImage = async (config: PromptConfig, seed?: number): Promise<string> => {
  const ai = getClient();
  const prompt = constructMedicalPrompt(config);
  
  const parts: any[] = [];
  
  // If reference image exists, add it to parts
  if (config.referenceImageUrl) {
    // Basic base64 cleanup if needed (though usually passed clean)
    const base64Data = config.referenceImageUrl.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, "");
    parts.push({ 
      inlineData: { 
        mimeType: 'image/png', // Assuming png/jpeg mostly, Gemini handles common formats
        data: base64Data 
      } 
    });
  }
  
  parts.push({ text: prompt });

  const requestConfig: any = {
    safetySettings: SAFETY_SETTINGS
  };
  
  if (seed !== undefined) {
    requestConfig.seed = seed;
    // Using a lower temperature for consistent series generation
    requestConfig.temperature = 0.7;
  }

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: { parts: parts },
      config: requestConfig
    });

    const part = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
    if (part?.inlineData?.data) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
    // Check if we got a text refusal or safety block
    const textPart = response.candidates?.[0]?.content?.parts?.find(p => p.text);
    if (textPart?.text) {
        throw new Error(`Model Refusal: ${textPart.text}`);
    }
    if (response.candidates?.[0]?.finishReason) {
        throw new Error(`Generation blocked. Reason: ${response.candidates[0].finishReason}`);
    }

    throw new Error("No image data found in response");
  } catch (error) {
    console.error("Gemini Image Gen Error:", error);
    throw error;
  }
};

// --- Series Generation ---
export const generateMedicalSeries = async (config: PromptConfig): Promise<GeneratedImage[]> => {
  // Generate a random seed to lock the subject identity/style across the series
  const seriesSeed = Math.floor(Math.random() * 1000000);

  // We will generate 3 variations in parallel
  const variations = [
    { ...config, label: 'Master View' }, // Standard
    { ...config, plane: 'Transverse/Axial', label: 'Cross-Section' }, // Cut
    { ...config, zoom: 'Macro (Histology)', label: 'Micro Structure' } // Zoom
  ];

  const promises = variations.map(async (conf) => {
    // Pass the shared seed
    const url = await generateMedicalImage(conf, seriesSeed);
    return {
      id: Date.now().toString() + Math.random().toString(),
      url,
      prompt: constructMedicalPrompt(conf),
      timestamp: Date.now(),
      label: conf.label,
      mediaType: 'IMAGE' as const
    };
  });

  return Promise.all(promises);
};

// --- Video Generation (Veo) ---
export const generateMedicalVideo = async (config: PromptConfig): Promise<string> => {
  const ai = getClient();
  const prompt = constructMedicalPrompt(config, true); // true for isVideo

  try {
    console.log("Starting Video Gen with prompt:", prompt);
    let operation = await ai.models.generateVideos({
      model: 'veo-3.1-fast-generate-preview',
      prompt: prompt,
      config: {
        numberOfVideos: 1,
        resolution: '1080p',
        aspectRatio: '16:9'
      }
    });

    // Polling
    console.log("Video operation started, polling...", operation);
    while (!operation.done) {
      await new Promise(resolve => setTimeout(resolve, 5000)); // 5s interval
      operation = await ai.operations.getVideosOperation({operation: operation});
    }

    const uri = operation.response?.generatedVideos?.[0]?.video?.uri;
    if (!uri) throw new Error("No video URI returned from generation operation.");

    console.log("Video generated, fetching URI:", uri);
    // Fetch bytes to create local URL
    const vidResponse = await fetch(`${uri}&key=${process.env.API_KEY}`);
    if (!vidResponse.ok) throw new Error(`Failed to fetch video content: ${vidResponse.statusText}`);
    
    const blob = await vidResponse.blob();
    return URL.createObjectURL(blob);

  } catch (error) {
    console.error("Gemini Video Gen Error:", error);
    throw error;
  }
};


// --- Iterative In-Painting / Editing ---
export const editMedicalImage = async (
  currentImageBase64: string, 
  instruction: string, 
  selection?: SelectionRect
): Promise<string> => {
  const ai = getClient();
  
  // Extract base64 data (remove header)
  const base64Data = currentImageBase64.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, "");
  
  let prompt = `Edit this medical image. Instruction: ${instruction}.`;
  
  // If a selection is provided, we guide the model with coordinates
  if (selection) {
    prompt += ` Focus changes specifically on the area around coordinates: top ${Math.round(selection.y)}%, left ${Math.round(selection.x)}%, covering roughly ${Math.round(selection.width)}% width. Maintain the rest of the image exactly as is.`;
  }
  
  prompt += " Maintain the same medical illustration style and resolution. Ensure no distorted text or biological inaccuracies.";

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          { inlineData: { mimeType: 'image/png', data: base64Data } },
          { text: prompt }
        ]
      },
      config: {
        safetySettings: SAFETY_SETTINGS
      }
    });

    const part = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
    if (part?.inlineData?.data) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
    throw new Error("No edited image returned");
  } catch (error) {
    console.error("Gemini Image Edit Error:", error);
    throw error;
  }
};

// --- Anonymize / HIPAA Scrubber ---
export const anonymizeImage = async (imageBase64: string): Promise<string> => {
  const ai = getClient();
  // Strip header
  const base64Data = imageBase64.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, "");

  // Specific prompt for de-identification
  const prompt = "Edit this image for privacy compliance. Detect any human faces and apply a heavy blur or pixelation to them to anonymize the individual. Do NOT alter any other parts of the image (medical equipment, anatomy, text, background). Preserve the original image quality and composition exactly.";

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          { inlineData: { mimeType: 'image/png', data: base64Data } },
          { text: prompt }
        ]
      },
      config: {
        safetySettings: SAFETY_SETTINGS
      }
    });

    const part = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
    if (part?.inlineData?.data) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
    throw new Error("No anonymized image returned");
  } catch (error) {
    console.error("Gemini Anonymization Error:", error);
    throw error;
  }
};
